import { request } from './http';
import {
  ApiKey,
  CreatedApiKey,
  Project,
  ProjectProfile,
  ProjectTrackingPlan,
  WebsiteDiscovery,
  WebsiteDiscoveryJob,
  ProjectGoal,
  ProjectGoalKind,
  ProjectGoalPathMatch,
  ProjectMonitoringTerms,
} from './types';

export const discoverWebsite = (websiteUrl: string) =>
  request<WebsiteDiscovery, { websiteUrl: string }>(
    '/project/discover',
    'POST',
    { websiteUrl },
  );

export const prepareSetupKey = (projectId: string) =>
  request<{ publicKey: string }>(
    `/project/${encodeURIComponent(projectId)}/setup-key`,
    'POST',
  );

export const prepareSetupPlan = (
  projectId: string,
  trackingPlan: ProjectTrackingPlan,
) =>
  request<
    { publicKey: string | null; profile: ProjectProfile; goals: ProjectGoal[] },
    { trackingPlan: ProjectTrackingPlan }
  >(`/project/${encodeURIComponent(projectId)}/setup-key`, 'POST', {
    trackingPlan,
  });

export const listProjects = () =>
  request<{ projects: Project[] }>('/project', 'GET');

export const getProject = (projectId: string) =>
  request<Project>(`/project/${encodeURIComponent(projectId)}`, 'GET');

export const createProject = (body: {
  name: string;
  timezone?: string;
  primaryDomain?: string | null;
  allowedHostnames?: string[] | null;
  profile?: ProjectProfile;
  allowDuplicate?: boolean;
}) => request<Project, typeof body>('/project', 'POST', body);

export const updateProject = (
  projectId: string,
  body: {
    name?: string;
    timezone?: string;
    primaryDomain?: string | null;
    allowedHostnames?: string[] | null;
    profile?: ProjectProfile;
  },
) =>
  request<Project, typeof body>(
    `/project/${encodeURIComponent(projectId)}`,
    'PATCH',
    body,
  );

export const deleteProject = (projectId: string) =>
  request<{ message: string }>(
    `/project/${encodeURIComponent(projectId)}`,
    'DELETE',
  );

export const listApiKeys = (projectId: string) =>
  request<{ apiKeys: ApiKey[] }>(
    `/project/${encodeURIComponent(projectId)}/api-keys`,
    'GET',
  );

/** The response carries the secret key. It is never retrievable again. */
export const createApiKey = (projectId: string, body: { name?: string }) =>
  request<CreatedApiKey, typeof body>(
    `/project/${encodeURIComponent(projectId)}/api-keys`,
    'POST',
    body,
  );

export const revokeApiKey = (projectId: string, apiKeyId: string) =>
  request<{ message: string }>(
    `/project/${encodeURIComponent(projectId)}/api-keys/${encodeURIComponent(apiKeyId)}`,
    'DELETE',
  );

export const listGoals = (projectId: string) =>
  request<{ goals: ProjectGoal[] }>(
    `/project/${encodeURIComponent(projectId)}/goals`,
    'GET',
  );

export const createGoal = (
  projectId: string,
  body: {
    name: string;
    kind: ProjectGoalKind;
    eventName?: string;
    pathPattern?: string;
    pathMatch?: ProjectGoalPathMatch;
    isPrimary?: boolean;
  },
) =>
  request<ProjectGoal, typeof body>(
    `/project/${encodeURIComponent(projectId)}/goals`,
    'POST',
    body,
  );

export const updateGoal = (
  projectId: string,
  goalId: string,
  body: { name?: string; isPrimary?: true },
) =>
  request<ProjectGoal, typeof body>(
    `/project/${encodeURIComponent(projectId)}/goals/${encodeURIComponent(goalId)}`,
    'PATCH',
    body,
  );

export const deleteGoal = (projectId: string, goalId: string) =>
  request<{ message: string }>(
    `/project/${encodeURIComponent(projectId)}/goals/${encodeURIComponent(goalId)}`,
    'DELETE',
  );

export const getMonitoringTerms = (projectId: string) =>
  request<ProjectMonitoringTerms>(
    `/project/${encodeURIComponent(projectId)}/monitoring-terms`,
    'GET',
  );

export const updateMonitoringTerms = (
  projectId: string,
  body: { terms: string[] },
) =>
  request<ProjectMonitoringTerms, typeof body>(
    `/project/${encodeURIComponent(projectId)}/monitoring-terms`,
    'PUT',
    body,
  );

export const startWebsiteDiscovery = (websiteUrl: string) =>
  request<WebsiteDiscoveryJob, { websiteUrl: string }>(
    '/project/discover/jobs',
    'POST',
    { websiteUrl },
  );
export const getWebsiteDiscovery = (jobId: string) =>
  request<WebsiteDiscoveryJob>(
    `/project/discover/jobs/${encodeURIComponent(jobId)}`,
    'GET',
  );
